import * as React from 'react';
import {useEffect, useState} from 'react';
import {ChevronDown, Play} from 'lucide-react';
import {AnimatePresence, motion} from 'framer-motion';
import {useNavigate} from 'react-router-dom';
import {BRAND} from '../brand';

// What we do, one line at a time.
const DIMENSIONS = BRAND.hero.dimensions;

const RotatingDimension: React.FC = () => {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setIndex((i) => (i + 1) % DIMENSIONS.length), 2600);
        return () => clearInterval(t);
    }, []);
    return (
        <>
            The Next Dimension of{' '}
            {/* Every term sits in the same grid cell: the invisible ones size the slot to the
                longest word, so nothing shifts and the visible one shares the text baseline. */}
            <span className="inline-grid text-left align-baseline">
                {DIMENSIONS.map((d) => (
                    <span key={d} aria-hidden className="invisible whitespace-nowrap [grid-area:1/1]">{d}</span>
                ))}
                <AnimatePresence mode="wait" initial={false}>
                    <motion.span
                        key={DIMENSIONS[index]}
                        className="whitespace-nowrap text-white [grid-area:1/1]"
                        initial={{y: 8, opacity: 0}}
                        animate={{y: 0, opacity: 1}}
                        exit={{y: -8, opacity: 0}}
                        transition={{duration: 0.3, ease: 'easeOut'}}
                    >
                        {DIMENSIONS[index]}
                    </motion.span>
                </AnimatePresence>
            </span>
        </>
    );
};

const Hero: React.FC = () => {
    const navigate = useNavigate();
    return (
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
            {/* Background Elements */}
            <div className="absolute inset-0 z-0">
                <div
                    className="absolute inset-0 bg-gradient-to-b from-lumina-dark via-[#0a0a1a] to-lumina-dark opacity-90"></div>
                <img
                    src="https://picsum.photos/seed/tech/1920/1080"
                    alt="Background"
                    className="w-full h-full object-cover opacity-20 mix-blend-overlay"
                />
                {/* Animated Orbs */}
                <div
                    className="absolute top-1/4 left-1/4 w-96 h-96 bg-lumina-accent rounded-full filter blur-[120px] opacity-20 animate-pulse-slow"></div>
                <div
                    className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-lumina-purple rounded-full filter blur-[120px] opacity-20 animate-pulse-slow delay-1000"></div>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
                <motion.div
                    initial={{opacity: 0, y: 30}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.8}}
                >
          <span
              className="inline-block px-4 py-1 mb-6 border border-gray-700 rounded-full bg-gray-900/50 text-lumina-accent text-xs font-semibold tracking-[0.2em] uppercase backdrop-blur-md">
            <RotatingDimension/>
          </span>

                    <h1 className="text-5xl md:text-7xl lg:text-9xl font-display font-black text-white mb-6 leading-tight tracking-tighter">
                        {BRAND.hero.headline[0]} <br/>
                        <span
                            className="text-transparent bg-clip-text bg-gradient-to-r from-lumina-accent via-white to-lumina-purple animate-pulse">
              {BRAND.hero.headline[1]}
            </span>
                    </h1>

                    <p className="max-w-2xl mx-auto text-gray-400 text-lg md:text-xl mb-10 font-light leading-relaxed">
                        {BRAND.hero.subhead}
                    </p>

                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <button
                            onClick={() => navigate('/start')}
                            className="px-8 py-4 bg-lumina-accent text-black font-display font-bold tracking-wider hover:bg-white transition-all duration-300 transform hover:scale-105 skew-x-[-10deg]">
                            <span className="skew-x-[10deg] inline-block">START PROJECT</span>
                        </button>
                        <button
                            className="group px-8 py-4 border border-gray-600 hover:border-white bg-transparent text-white font-display font-bold tracking-wider transition-all duration-300 flex items-center gap-2 skew-x-[-10deg]">
              <span className="skew-x-[10deg] inline-block flex items-center gap-2">
                <Play className="w-4 h-4 fill-white group-hover:text-lumina-accent"/>
                SHOWREEL
              </span>
                        </button>
                    </div>
                </motion.div>
            </div>

            <motion.div
                className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-gray-500 animate-bounce"
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                transition={{delay: 1, duration: 1}}
            >
                <ChevronDown className="w-8 h-8"/>
            </motion.div>
        </section>
    );
};

export default Hero;